import { useState, useEffect } from 'react';
import { Table, Space, Drawer, Button, Popover, Input } from 'antd';
import { EditOutlined, FileSearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import urlJoin from 'url-join';
import restful from '../../core/utilities/restful';
import { ResourceNames, DrawerState } from '../constants';

import axiosErrorHandler from '../../core/utilities/axiosErrorHandler';
import { IIdea } from '../types';
import AuthorIdeaCreateView from './AuthorIdeaCreateView';
import AuthorIdeaEditView from './AuthorIdeaEditView';
import { Routes } from '../../routes';

const { Search } = Input;

export default () => {
  const [ideas, setIdeas] = useState<Array<IIdea>>([]);
  const [latestIdea, setLatestIdea] = useState<IIdea | null>(null);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [drawerState, setDrawerState] = useState<DrawerState>(
    DrawerState.closed
  );
  const [pk, setPk] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalIdeas, setTotalIdeas] = useState(0);
  const [stockTickerFilter, setStockTickerFilter] = useState('');
  const navigate = useNavigate();

  const ideaAPI = restful(ResourceNames.ideas);

  const columns = [
    {
      width: '7%',
      title: 'Stock ticker',
      dataIndex: 'stockTicker',
      key: 'stockTicker',
      render: (stockTicker: string) => stockTicker,
    },
    {
      width: '22%',
      title: 'Exchange',
      dataIndex: 'exchangeName',
      key: 'exchangeName',
      render: (exchangeName: string) => exchangeName,
    },
    {
      width: '7%',
      title: 'Stock price currency',
      dataIndex: 'stockPriceCurrency',
      key: 'stockPriceCurrency',
      render: (stockPriceCurrency: string) => stockPriceCurrency,
    },
    {
      width: '5%',
      title: 'Type',
      dataIndex: 'ideaType',
      key: 'ideaType',
      render: (ideaType: string) => ideaType,
    },
    {
      width: '7%',
      title: 'Time horizon',
      dataIndex: 'timeHorizon',
      key: 'timeHorizon',
      render: (timeHorizon: string) => timeHorizon,
    },
    {
      width: '7%',
      title: 'Conviction',
      dataIndex: 'conviction',
      key: 'conviction',
      render: (conviction: string) => conviction,
    },
    {
      width: '8%',
      title: 'Target price',
      dataIndex: 'targetPrice',
      key: 'targetPrice',
      render: (targetPrice: string) => targetPrice,
    },
    {
      width: '8%',
      title: 'Investment amount',
      dataIndex: 'investmentAmount',
      key: 'investmentAmount',
      render: (investmentAmount: string) => investmentAmount,
    },
    {
      width: '7%',
      title: 'Investment currency',
      dataIndex: 'investmentCurrency',
      key: 'investmentCurrency',
      render: (investmentCurrency: string) => investmentCurrency,
    },
    {
      width: '14%',
      title: 'Style',
      dataIndex: 'style',
      key: 'style',
      render: (style: string) => style,
    },
    {
      width: '5%',
      title: 'Is open?',
      dataIndex: 'isOpen',
      key: 'isOpen',
      render: (isOpen: string) => (isOpen ? 'Yes' : 'No'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: { id: number; isOpen: boolean }) => (
        <Space size='middle'>
          <Popover content='view/edit' trigger='hover'>
            <a>
              <EditOutlined onClick={onEditIdea(record)} />
            </a>
          </Popover>
          <Popover content='view history' trigger='hover'>
            <a onClick={onViewIdeaHistory(record)}>
              <FileSearchOutlined />
            </a>
          </Popover>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await updateIdeas(1);
      } catch (err: any) {
        axiosErrorHandler(err, true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateIdeas = async (page: number, stockTicker?: string) => {
    const ideasResponse = await getIdeasResponse(page, stockTicker);
    setIdeas(
      ideasResponse.data.results.map((idea: IIdea) => ({
        ...idea,
        key: idea.id,
      }))
    );
    if (page === 1 && ideasResponse.data.results.length > 0) {
      setLatestIdea({
        ...ideasResponse.data.results[0],
        key: ideasResponse.data.results[0].id,
      });
    }
    setTotalIdeas(ideasResponse.data.count);
  };

  const getIdeasResponse = async (page: number, stockTicker?: string) =>
    await ideaAPI.list({ params: { page, stockTicker: stockTicker || '' } });

  const onEditIdea =
    ({ id }: { id: number }) =>
    () => {
      setLoading(true);
      setPk(id);
      setTitle('Edit idea');
      setDrawerState(DrawerState.ideaEdit);
      setLoading(false);
    };

  const onViewIdeaHistory =
    ({ id }: { id: number }) =>
    () => {
      const url = urlJoin(Routes.ideas, `${id}`, 'history');
      navigate(url);
    };

  const onCreateIdea = () => {
    setLoading(true);
    setPk(null);
    setTitle('Create idea');
    setDrawerState(DrawerState.ideaCreate);
    setLoading(false);
  };

  const onCreateIdeaPostProcess = async () => {
    try {
      setLoading(true);
      await updateIdeas(1);
    } catch (err: any) {
      axiosErrorHandler(err, true);
    } finally {
      setLoading(false);
    }
  };

  const getIdea = (pk: number) =>
    ideas.find(idea => idea.id === pk) || ideas[0];

  const onChangePage = async (page: number, pageSize: number) => {
    try {
      setLoading(true);
      await updateIdeas(page, stockTickerFilter);
      setCurrentPage(page);
    } catch (err: any) {
      axiosErrorHandler(err, true);
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    setDrawerState(DrawerState.closed);
  };

  const onSearchStockTicker = async (value: string) => {
    try {
      setStockTickerFilter(value);
      setLoading(true);
      await updateIdeas(1, value);
    } catch (err: any) {
      axiosErrorHandler(err, true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Space direction='horizontal'>
        <Button onClick={onCreateIdea} type='primary'>
          Create idea
        </Button>
        <Search
          placeholder='Search stock ticker'
          onSearch={onSearchStockTicker}
          enterButton
        />
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={ideas}
        pagination={{
          showQuickJumper: true,
          onChange: onChangePage,
          total: totalIdeas,
          current: currentPage,
          pageSize: 10,
        }}
      />
      <Drawer
        title={title}
        width={window.innerWidth * 0.5}
        onClose={onClose}
        visible={drawerState !== DrawerState.closed}
      >
        {pk !== null && drawerState === DrawerState.ideaEdit && (
          <AuthorIdeaEditView
            onSubmitPostProcess={onCreateIdeaPostProcess}
            ideaData={getIdea(pk)}
            closeDrawer={onClose}
          />
        )}
        {drawerState === DrawerState.ideaCreate && (
          <AuthorIdeaCreateView
            onSubmitPostProcess={onCreateIdeaPostProcess}
            closeDrawer={onClose}
            latestIdea={latestIdea}
          />
        )}
      </Drawer>
    </div>
  );
};
